import { cloneDeep, isNumber, throttle } from 'lodash-es';
import { mapActions } from 'pinia';
import { pixelsToHeight } from '@/lib/utils/grid';
import { bus } from '@/lib/eventBus';
import autoSizeEnabled from '@/mixins/autoSizeEnabled';
import {
  useEditorStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

export default {
  mixins: [autoSizeEnabled],
  created() {
    this.throttleResize = throttle(this.resize.bind(this), 100);

    bus.on('module:resize', this.onModuleResizing);
    bus.on('module:added', this.onModuleAdded);
    bus.on('module:updated', this.onModuleUpdated);
  },
  beforeUnmount() {
    bus.off('module:resize', this.onModuleResizing);
    bus.off('module:added', this.onModuleAdded);
    bus.off('module:updated', this.onModuleUpdated);
  },
  watch: {
    shape() {
      if (this.autoSizeEnabled(this.moduleData) && !this.hasShape(this.moduleData) && this.active) {
        this.throttleResize();
      }
    },
    overflow() {
      if (this.autoSizeEnabled(this.moduleData) && this.active) {
        this.throttleResize();
      }
    },
  },
  computed: {
    shape() {
      return this.moduleData?.options?.shape;
    },
    overflow() {
      return this.moduleData?.options?.overflow;
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.UPDATE_MODULES,
    ]),
    onModuleResizing(moduleId) {
      if (this.id === moduleId && this.active && this.autoSizeEnabled(this.moduleData)) {
        this.resizing = true;
        this.throttleResize();
      }
    },
    onModuleAdded({ id, isLocal }) {
      if (
        id !== this.id
        || !isLocal
        || !this.autoSizeEnabled(this.moduleData)
      ) {
        return;
      }
      this.throttleResize();
    },
    onModuleUpdated({ updatedModule, isLocal, isLocalNotUndoable }) {
      if (
        updatedModule?.id !== this.id
        || !isLocal
        || isLocalNotUndoable
        || !this.autoSizeEnabled(this.moduleData)
      ) {
        return;
      }
      this.resizing = false;
      this.throttleResize();
    },
    resize() {
      /*
        Wait two cycles to ensure changes to DOM have been rendered
        before calculating the size of the content.
      */
      this.$nextTick(() => {
        this.$nextTick(() => {
          const textEditor = this.$refs?.textEditor;
          if (!textEditor) return;
          const contentHeight = textEditor.getContentHeight();
          if (!isNumber(contentHeight)) return;

          let adjustedHeight = contentHeight;
          if (this.canvasName === 'notes') {
            /*
              Allow the text content to bleed into the text editor padding
              by up to 4 pixels. This means that we would rather have the text
              module be a little small than add an entire extra row.
            */
            const contentPaddingOverlap = 4;
            // Convert pixel height to height in grid rows.
            adjustedHeight = pixelsToHeight(contentHeight - contentPaddingOverlap);
          }
          const isResizing = this.resizing && this.layoutData;
          const height = isResizing
            ? this.layoutData.h
            : this.moduleData?.layout?.height;
          const resizeModule = (adjustedHeight > height)
            || (adjustedHeight < height && this.autoSizeShrinkEnabled(this.moduleData));
          if (resizeModule) {
            if (isResizing) {
              /*
                If we're in a resize drag operation then don't update the app store, but
                instead only update the layout object being used by the grid layout so we can see
                the current size of the module rendered in the UI. App store
                will be updated when resize finishes.
              */
              this.layoutData.h = adjustedHeight;
              bus.emit('module:auto-resizing-y', { updatedModule: this.moduleData });
            } else {
              /*
                Update the app store with the module's new size if needed.
              */
              const data = cloneDeep(this.moduleData);
              data.layout.height = adjustedHeight;
              this[types.UPDATE_MODULES]({
                canvasName: this.canvasName,
                modules: [data],
                canUndo: false, // Ensure autoresize changes are not undoable
              });
            }
          }
        });
      });
    },
  },
};
